import React from 'react'
import { useFormik } from 'formik'
import { FormattedMessage, useIntl } from 'react-intl'
import {
  Button,
  FormControl,
  FormHelperText,
  InputLabel,
  TextField
} from '@material-ui/core'
import CheckCircleIcon from '@material-ui/icons/CheckCircle'
import { EinvoiceDelegateFormValidation } from '../../../../utils/validationSchema'
import { FormLoader } from '../../../Loader/FormLoader'
import { AuthenticatedUserConsumer } from '../../../../AuthenticatedUserContext'
import PhoneInput from 'react-phone-input-2'
import 'react-phone-input-2/lib/material.css'
import ar from 'react-phone-input-2/lang/ar.json'
import { LanguageConsumer } from '../../../../LanguageContext'
import { OTPCaptchaProvider } from '../../../../OTPCaptcha'
import ChangePhone from '../../EditProfile/AdditionalInfo/ChangePhone'
import ChangeEmail from '../../EditProfile/AdditionalInfo/ChangeEmail'
import { useStyles } from './Styles'

const EinvoiceDelegateForm = ({
  onSubmit,
  resetStatus,
  delegateData,
  delegateLoading,
  getEinvoiceDelegate
}) => {
  const classes = useStyles()
  const intl = useIntl()

  const [emailVerified, setEmailVerified] = React.useState(false)
  const [phoneVerified, setPhoneVerified] = React.useState(false)

  const form = useFormik({
    initialValues: {
      name_en: '',
      name_ar: '',
      national_id: '',
      phone: '20',
      newPhone: '',
      phoneCountryCode: '',
      VerificationCode: '',
      email: '',
      VerificationEmailCode: ''
    },
    validateOnBlur: false,
    validateOnChange: true,
    isInitialValid: false,
    validateOnMount: true,
    validationSchema: EinvoiceDelegateFormValidation,
    onSubmit: (values, actions) => {
      onSubmit(values, actions, emailVerified)
    }
  })

  React.useEffect(() => {
      if (!!resetStatus) {
        resetStatus()
      }
      if (!!getEinvoiceDelegate && !delegateData) {
        getEinvoiceDelegate()
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [])

  React.useEffect(() => {
      if (!!delegateData) {
        form.setValues({
          name_en: delegateData.name_en || '',
          name_ar: delegateData.name_ar || '',
          national_id: delegateData.national_id || '',
          phone: delegateData.phone
            ? delegateData.phone.replace(/\+/g, '')
            : '20',
          newPhone: '',
          phoneCountryCode: '',
          VerificationCode: '',
          email: delegateData.email || '',
          VerificationEmailCode: ''
        })
        if (delegateData.email_verified) {
          setEmailVerified(true)
        }
        if (delegateData.phone_verified) {
          setPhoneVerified(true)
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [delegateData])

  const isSubmitDisabled = !form.isValid || form.isSubmitting || !emailVerified || !form.values.phone || form.values.phone === '20'

  return (
    <AuthenticatedUserConsumer>
      {({ resetPhoneStatus, resetEmailStatus }) => (
        <form onSubmit={form.handleSubmit} noValidate autoComplete="off" className={classes.formWrapper}>

          <div className={classes.AdditionalInfoRow}>
            <InputLabel id="einvoiceDelegate.name_en">
              <FormattedMessage id='EinvoiceDelegate.Fields.label.nameEn'/>
            </InputLabel>
            <FormControl variant="outlined">
              <TextField
                placeholder={intl.formatMessage({ id: 'EinvoiceDelegate.Fields.placeholder.nameEn' })}
                id="einvoiceDelegate.name_en"
                type='text'
                margin="dense"
                variant="outlined"
                name='name_en'
                onChange={form.handleChange}
                onBlur={form.handleBlur}
                value={form.values.name_en}
                helperText={form.errors.name_en && form.touched.name_en && form.errors.name_en}
                error={!!form.errors.name_en && form.touched.name_en !== undefined}
              />
            </FormControl>
          </div>

          <div className={classes.AdditionalInfoRow}>
            <InputLabel id="einvoiceDelegate.name_ar">
              <FormattedMessage id='EinvoiceDelegate.Fields.label.nameAr'/>
            </InputLabel>
            <FormControl variant="outlined">
              <TextField
                placeholder={intl.formatMessage({ id: 'EinvoiceDelegate.Fields.placeholder.nameAr' })}
                id="einvoiceDelegate.name_ar"
                type='text'
                margin="dense"
                variant="outlined"
                name='name_ar'
                onChange={form.handleChange}
                onBlur={form.handleBlur}
                value={form.values.name_ar}
                helperText={form.errors.name_ar && form.touched.name_ar && form.errors.name_ar}
                error={!!form.errors.name_ar && form.touched.name_ar !== undefined}
              />
            </FormControl>
          </div>

          <div className={classes.AdditionalInfoRow}>
            <InputLabel id="einvoiceDelegate.national_id">
              <FormattedMessage id='EinvoiceDelegate.Fields.label.nationalId'/>
            </InputLabel>
            <FormControl variant="outlined">
              <TextField
                placeholder={intl.formatMessage({ id: 'EinvoiceDelegate.Fields.placeholder.nationalId' })}
                id="einvoiceDelegate.national_id"
                type='text'
                margin="dense"
                variant="outlined"
                name='national_id'
                onChange={form.handleChange}
                onBlur={form.handleBlur}
                value={form.values.national_id}
                helperText={form.errors.national_id && form.touched.national_id && form.errors.national_id}
                error={!!form.errors.national_id && form.touched.national_id !== undefined}
              />
            </FormControl>
          </div>

          <div className={classes.AdditionalInfoRow}>
            <InputLabel id="einvoiceDelegate.phone">
              <FormattedMessage id='Fields.label.phone2'/>
            </InputLabel>
            <div className={classes.AdditionalInfoPhoneRow}>
              <LanguageConsumer>
                {({ locale }) => (
                  <div className={classes.PhoneInputWrapper}
                       onClick={() => form.setFieldTouched('phone', true, true)}>
                    <PhoneInput
                      placeholder={intl.formatMessage({ id: 'AdditionalInfo.placeholder.phone' })}
                      enableSearch={true}
                      disabled={true}
                      country={'eg'}
                      localization={locale === 'ar' ? ar : ''}
                      specialLabel={''}
                      excludeCountries={['il']}
                      inputProps={{ name: 'phone', required: true }}
                      searchPlaceholder={intl.formatMessage({ id: 'PhoneInput.Search.placeholder' })}
                      searchNotFound={intl.formatMessage({ id: 'PhoneInput.Search.noResults' })}
                      onChange={form.handleChange}
                      onBlur={form.handleBlur}
                      value={form.values.phone}
                    />
                    {!!form.errors.phone && form.touched.phone &&
                    <FormHelperText error={true}>{form.errors.phone}</FormHelperText>}
                  </div>
                )}
              </LanguageConsumer>
              {phoneVerified && <CheckCircleIcon className={classes.VerifiedIcon}/>}
              <OTPCaptchaProvider>
                <ChangePhone
                  additionalInfoForm={form}
                  resetPhoneStatus={resetPhoneStatus}
                  isDelegateContact={true}
                  countryCodeKey={'phoneCountryCode'}
                  buttonLabel={<FormattedMessage id='EinvoiceDelegate.VerifyPhone.button'/>}
                  onPhoneVerified={() => setPhoneVerified(true)}
                />
              </OTPCaptchaProvider>
            </div>
          </div>

          <div className={classes.AdditionalInfoRow}>
            <InputLabel id="einvoiceDelegate.email">
              <FormattedMessage id='Fields.label.taxEmail'/>
            </InputLabel>
            <div className={classes.EmailWrapper}>
              <FormControl variant="outlined">
                <TextField
                  placeholder={intl.formatMessage({ id: 'linkCompanyForm.placeholder.email' })}
                  id="einvoiceDelegate.email"
                  type='email'
                  margin="dense"
                  variant="outlined"
                  name='email'
                  disabled={true}
                  onChange={form.handleChange}
                  onBlur={form.handleBlur}
                  value={form.values.email}
                  helperText={form.errors.email && form.touched.email && form.errors.email}
                  error={!!form.errors.email && form.touched.email !== undefined}
                />
              </FormControl>
              {emailVerified && <CheckCircleIcon className={classes.VerifiedIcon}/>}
              <OTPCaptchaProvider>
                <ChangeEmail
                  additionalInfoForm={form}
                  resetEmailStatus={resetEmailStatus}
                  onEmailVerified={() => setEmailVerified(true)}
                />
              </OTPCaptchaProvider>
            </div>
          </div>

          <div className={classes.SubmitRow}>
            <Button
              size="large"
              variant="contained"
              color="primary"
              type="submit"
              disabled={isSubmitDisabled}
              className={classes.AdditionalInfoSubmit}
            >
              <FormattedMessage id='EinvoiceDelegate.submit'/>
            </Button>
          </div>

          {form.isSubmitting && <FormLoader loading={true}/>}
        </form>
      )}
    </AuthenticatedUserConsumer>
  )
}

export default EinvoiceDelegateForm
